/**
 * Cart Drawer — Pitchers Only
 * Replaces Rebuy Smart Cart. Handles all dynamic behavior for cart-drawer.liquid.
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  // ============================================================
  // Section A — State & Config
  // ============================================================

  var CONFIG = null;
  var CHECKOUT_PLUS_VARIANT = 44979238305999;

  var state = {
    cart: null,
    isOpen: false,
    productCache: {},
    collectionCache: {},
    isAutoAdding: false,
    isRendering: false,
    lastUpsellProductIds: '',
    cartFetchedAt: 0,
    cpAddedThisSession: false,
    // (rule, product) pairs already sent as an 'impression' in the current drawer
    // open. Reset when the drawer goes from closed to open, and on close.
    impressionsSent: {}
  };

  var TRASH_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>';

  // ── Diagnostic logging — off in production. Set to true locally to debug. ──
  var DEBUG = false;
  function dbg() {
    if (DEBUG) console.log.apply(console, ['[PO-Cart]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ── Analytics tracking ──
  var PO_ANALYTICS_URL = 'https://dashboard-production-d03c.up.railway.app';

  // ruleId is the real rules.id from cart-config.json (the PO Upsell Dashboard
  // publishes it as `id` on every rule). It used to be the 0-based position of
  // the rule in cart-config.json, which pointed attribution at the wrong rule.
  // A rule with no id sends rule_id null plus its index as a label.
  function trackCartEvent(eventType, ruleId, productId, ruleIndex) {
    if (!PO_ANALYTICS_URL) return;
    var rid = (ruleId === undefined || ruleId === null || ruleId === '') ? null : ruleId;
    var payload = {
      widget_id: 1,
      rule_id: rid,
      event_type: eventType,
      product_id: productId,
      session_id: getCartSessionId(),
    };
    if (rid === null && ruleIndex !== undefined && ruleIndex !== null && ruleIndex !== '') {
      payload.metadata = { rule_label: 'cart-config-index-' + ruleIndex };
    }
    fetch(PO_ANALYTICS_URL + '/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(function() {});
  }

  // renderUpsell runs on every cart re-render (add-to-cart fires both the fetch
  // interceptor and the theme's cart:change, an upsell add re-renders twice, qty
  // changes re-render), so an unguarded impression call counted the same card
  // 2-3x per drawer open. One impression per (rule, product) per drawer open.
  function impressionKey(ruleId, productId, ruleIndex) {
    var rid = (ruleId === undefined || ruleId === null || ruleId === '') ? 'idx' + ruleIndex : String(ruleId);
    return rid + ':' + productId;
  }

  function trackImpressionOnce(ruleId, productId, ruleIndex) {
    var k = impressionKey(ruleId, productId, ruleIndex);
    if (state.impressionsSent[k]) return false;
    state.impressionsSent[k] = true;
    trackCartEvent('impression', ruleId, productId, ruleIndex);
    return true;
  }

  function getCartSessionId() {
    var sid = sessionStorage.getItem('po_cart_sid');
    if (!sid) {
      sid = 'cart-' + Math.random().toString(36).slice(2) + Date.now();
      sessionStorage.setItem('po_cart_sid', sid);
    }
    return sid;
  }

  function loadConfig() {
    var configUrl = window.__PO_CART_CONFIG_URL || '/assets/cart-config.json';
    dbg('Loading config from:', configUrl);
    return fetch(configUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        CONFIG = data;
        CHECKOUT_PLUS_VARIANT = CONFIG.checkout_plus.variant_id || CHECKOUT_PLUS_VARIANT;
        dbg('Config loaded successfully');
        return CONFIG;
      })
      .catch(function (err) {
        console.error('[CartDrawer] Failed to load config:', err);
      });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function formatDollars(dollars) {
    return '$' + Number(dollars).toFixed(2);
  }

  // ============================================================
  // Section B — Drawer Open/Close
  // ============================================================

  function getUpsellDiscountForItem(item) {
    // Check if this cart item matches an upsell product with a discount
    if (!CONFIG || !CONFIG.upsell || !CONFIG.upsell.rules) return 0;
    var handle = item.handle || '';
    for (var i = 0; i < CONFIG.upsell.rules.length; i++) {
      var rule = CONFIG.upsell.rules[i];
      if (rule.discount_percent && rule.products) {
        if (rule.products.indexOf(handle) !== -1) {
          return rule.discount_percent;
        }
      }
    }
    return 0;
  }

  function getCheckoutUrl() {
    // Automatic discount handles upsell pricing — no code needed
    return '/checkout';
  }

  function resetButtonStates() {
    var btnPlus = document.getElementById('po-cart-btn-plus');
    var btnWithout = document.getElementById('po-cart-btn-without');
    if (btnPlus) {
      btnPlus.style.pointerEvents = '';
      btnPlus.innerHTML = 'CHECKOUT+ <span class="po-cart-btn-divider">|</span> <span id="po-cart-btn-plus-total">$0.00</span>';
    }
    if (btnWithout) {
      btnWithout.textContent = 'CONTINUE WITHOUT FREE RETURNS';
      btnWithout.style.pointerEvents = '';
    }
  }

  function openDrawer() {
    dbg('openDrawer() called');
    var drawer = document.getElementById('po-cart-drawer');
    var overlay = document.getElementById('po-cart-overlay');
    if (!drawer || !overlay) { dbg('ERROR: drawer or overlay element not found'); return; }

    // A fresh open (closed -> open) starts a new impression window. A second
    // openDrawer() while already open (cart:change + fetch interceptor both fire
    // on one add-to-cart) must not reset it.
    if (!state.isOpen) state.impressionsSent = {};

    // Save scroll position before locking (iOS needs position:fixed which resets scroll)
    state.scrollY = window.scrollY;
    document.body.style.top = '-' + state.scrollY + 'px';

    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.classList.add('po-cart-open');
    state.isOpen = true;
    resetButtonStates();

    // Force fresh fetch on every open
    fetchCart(true).then(function (cart) {
      renderCart(cart);
    });
  }

  function closeDrawer() {
    var drawer = document.getElementById('po-cart-drawer');
    var overlay = document.getElementById('po-cart-overlay');
    if (!drawer || !overlay) return;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.classList.remove('po-cart-open');
    // Restore scroll position
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY || 0);
    state.isOpen = false;
    state.impressionsSent = {};
  }

  function setupDrawerListeners() {
    // Overlay click
    var overlay = document.getElementById('po-cart-overlay');
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }

    // Close button
    var closeBtn = document.querySelector('.po-cart-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.isOpen) {
        closeDrawer();
      }
    });

    // Cart triggers — intercept links to /cart
    // Use CAPTURE phase so we fire BEFORE the theme's handlers
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href="/cart"]');
      var matchedBy = link ? 'a[href="/cart"]' : null;
      if (!link) { link = e.target.closest('a[href$="/cart"]'); matchedBy = link ? 'a[href$="/cart"]' : null; }
      if (!link) { link = e.target.closest('[aria-controls="cart-drawer"]'); matchedBy = link ? '[aria-controls="cart-drawer"]' : null; }
      if (!link) { link = e.target.closest('.cart-icon-bubble'); matchedBy = link ? '.cart-icon-bubble' : null; }
      if (link) {
        dbg('CAPTURED click on:', matchedBy, link.tagName, link.className);
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }
    }, true);
  }

  // ============================================================
  // Section C — Fetch Cart & Render
  // ============================================================

  function fetchCart(forceRefresh) {
    dbg('fetchCart() called, force:', !!forceRefresh);
    // Cache cart for 3 seconds to avoid hammering the API
    var now = Date.now();
    if (!forceRefresh && state.cart && state.cartFetchedAt && (now - state.cartFetchedAt < 3000)) {
      dbg('Using cached cart data');
      return Promise.resolve(state.cart);
    }
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { dbg('cart.js response status:', r.status); return r.json(); })
      .then(function (cart) {
        dbg('Cart loaded:', cart.item_count, 'items, total:', cart.total_price);
        state.cart = cart;
        state.cartFetchedAt = Date.now();
        return cart;
      })
      .catch(function (err) {
        console.error('[CartDrawer] Failed to fetch cart:', err);
        return state.cart;
      });
  }

  function renderCart(cart) {
    if (!cart || !CONFIG) return;
    state.isRendering = true;

    var displayItems = cart.items.filter(function (item) {
      return item.variant_id !== CHECKOUT_PLUS_VARIANT;
    });
    var displayCount = displayItems.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);

    var emptyEl = document.getElementById('po-cart-empty');
    var bodyEl = document.querySelector('.po-cart-body');
    var footerEl = document.querySelector('.po-cart-footer');

    if (displayCount === 0) {
      // Auto-remove Checkout+ if no regular products left
      var cpInCart = cart.items.find(function (item) { return item.variant_id === CHECKOUT_PLUS_VARIANT; });
      if (cpInCart) {
        var updates = {};
        updates[cpInCart.key] = 0;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          updateHeaderCartCount(0);
        }).catch(function () {});
      }

      // Empty state
      if (emptyEl) emptyEl.classList.add('is-visible');
      if (bodyEl) bodyEl.style.display = 'none';
      if (footerEl) footerEl.style.display = 'none';

      // Update empty state content from config
      if (CONFIG.empty_cart) {
        var heading = document.getElementById('po-cart-empty-heading');
        var message = document.getElementById('po-cart-empty-message');
        var link = document.getElementById('po-cart-empty-link');
        if (heading) heading.textContent = CONFIG.empty_cart.heading;
        if (message) message.textContent = CONFIG.empty_cart.message;
        if (link) {
          link.textContent = CONFIG.empty_cart.link_text;
          link.href = CONFIG.empty_cart.link_url;
        }
      }
    } else {
      if (emptyEl) emptyEl.classList.remove('is-visible');
      if (bodyEl) bodyEl.style.display = '';
      if (footerEl) footerEl.style.display = '';

      renderItems(cart.items);
      renderSubtotal(cart);
      renderProgressBar(cart);
      renderCheckoutPlus(cart);
      renderButtons(cart);

      // Only re-render upsell if cart products changed (avoids wiping dropdown mid-selection)
      // Delay upsell fetch to avoid piling up API calls with the initial cart fetch
      var currentProductIds = displayItems.map(function(i) { return i.product_id; }).sort().join(',');
      dbg('Upsell check — current:', currentProductIds, 'last:', state.lastUpsellProductIds, 'match:', currentProductIds === state.lastUpsellProductIds);
      if (currentProductIds !== state.lastUpsellProductIds) {
        state.lastUpsellProductIds = currentProductIds;
        dbg('Upsell setTimeout set (100ms)');
        setTimeout(function () {
          dbg('Upsell setTimeout fired');
          renderUpsell(cart);
        }, 100);
      } else {
        dbg('Upsell skipped — products unchanged');
      }
    }

    renderAnnouncement();
    updateHeaderCartCount(displayCount);

    state.isRendering = false;
  }

  function updateHeaderCartCount(count) {
    var badges = document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count], cart-count');
    badges.forEach(function (el) {
      el.textContent = count;
      // Show/hide badge based on count
      if (count > 0) {
        el.style.display = '';
        el.removeAttribute('hidden');
      } else {
        el.style.display = 'none';
      }
    });
    // Tell the theme to update its own cart state
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // ============================================================
  // Section D — Render Line Items
  // ============================================================

  function renderItems(items) {
    var container = document.getElementById('po-cart-items');
    if (!container) return;

    var html = '';
    items.forEach(function (item) {
      // Show Checkout+ as a regular line item (so customer can remove it)
      // but hide quantity controls — it's always qty 1

      var variantHtml = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantHtml = '<p class="po-cart-item-variant">' + escapeHtml(item.variant_title) + '</p>';
      }

      var priceHtml = '';
      var finalPrice = item.final_line_price;
      var originalPrice = item.original_line_price;

      // Automatic discount handles upsell pricing — just show what Shopify returns
      if (originalPrice > finalPrice) {
        priceHtml = '<span class="po-cart-item-price-sale">' + formatMoney(finalPrice) + '</span>' +
          '<span class="po-cart-item-price-compare">' + formatMoney(originalPrice) + '</span>';
      } else {
        priceHtml = '<span>' + formatMoney(finalPrice) + '</span>';
      }

      var imgSrc = item.image || '';
      var isCP = item.variant_id === CHECKOUT_PLUS_VARIANT;

      // For Checkout+ item: no quantity controls, just price and remove
      var qtyHtml = '';
      if (isCP) {
        qtyHtml = '<span style="font-size:12px;color:#666;">Qty: 1</span>';
      } else {
        qtyHtml = '<div class="po-cart-item-qty">' +
          '<button class="po-cart-qty-minus" data-key="' + item.key + '" data-qty="' + item.quantity + '">\u2212</button>' +
          '<span>' + item.quantity + '</span>' +
          '<button class="po-cart-qty-plus" data-key="' + item.key + '" data-qty="' + item.quantity + '">+</button>' +
        '</div>';
      }

      html += '<div class="po-cart-item" data-key="' + item.key + '">' +
        '<img class="po-cart-item-image" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(item.product_title) + '" />' +
        '<div class="po-cart-item-details">' +
          '<p class="po-cart-item-title">' + escapeHtml(item.product_title) + '</p>' +
          variantHtml +
          '<div class="po-cart-item-bottom">' +
            qtyHtml +
            '<div class="po-cart-item-price">' + priceHtml + '</div>' +
          '</div>' +
        '</div>' +
        '<button class="po-cart-item-remove" data-key="' + item.key + '">' + TRASH_SVG + '</button>' +
      '</div>';
    });

    container.innerHTML = html;
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ============================================================
  // Section E — Quantity & Remove
  // ============================================================

  function setupQuantityListeners() {
    var container = document.getElementById('po-cart-items');
    if (!container) return;

    container.addEventListener('click', function (e) {
      var minus = e.target.closest('.po-cart-qty-minus');
      if (minus) {
        var key = minus.getAttribute('data-key');
        var qty = parseInt(minus.getAttribute('data-qty'), 10);
        var newQty = qty - 1;
        optimisticQuantityUpdate(minus.closest('.po-cart-item'), key, newQty);
        updateQuantity(key, newQty);
        return;
      }

      var plus = e.target.closest('.po-cart-qty-plus');
      if (plus) {
        var key = plus.getAttribute('data-key');
        var qty = parseInt(plus.getAttribute('data-qty'), 10);
        var newQty = qty + 1;
        optimisticQuantityUpdate(plus.closest('.po-cart-item'), key, newQty);
        updateQuantity(key, newQty);
        return;
      }

      var remove = e.target.closest('.po-cart-item-remove');
      if (remove) {
        var key = remove.getAttribute('data-key');
        var itemEl = remove.closest('.po-cart-item');
        if (itemEl) {
          itemEl.style.opacity = '0.3';
          itemEl.style.transition = 'opacity 0.15s';
        }
        updateQuantity(key, 0);
        return;
      }
    });
  }

  function optimisticQuantityUpdate(itemEl, key, newQty) {
    if (!itemEl) return;
    if (newQty <= 0) {
      // Fade out for removal
      itemEl.style.opacity = '0.3';
      itemEl.style.transition = 'opacity 0.15s';
      return;
    }
    // Update the quantity display immediately
    var qtySpan = itemEl.querySelector('.po-cart-item-qty span');
    if (qtySpan) qtySpan.textContent = newQty;
    // Update data attributes for next click
    var minusBtn = itemEl.querySelector('.po-cart-qty-minus');
    var plusBtn = itemEl.querySelector('.po-cart-qty-plus');
    if (minusBtn) minusBtn.setAttribute('data-qty', newQty);
    if (plusBtn) plusBtn.setAttribute('data-qty', newQty);
    // Update price optimistically if we know the unit price
    if (state.cart) {
      var cartItem = state.cart.items.find(function (i) { return i.key === key; });
      if (cartItem) {
        var unitPrice = cartItem.final_line_price / cartItem.quantity;
        var newLinePrice = unitPrice * newQty;
        var priceEl = itemEl.querySelector('.po-cart-item-price');
        if (priceEl) {
          var saleSpan = priceEl.querySelector('.po-cart-item-price-sale');
          var plainSpan = priceEl.querySelector('span:not(.po-cart-item-price-compare):not(.po-cart-item-price-sale)');
          if (saleSpan) saleSpan.textContent = formatMoney(newLinePrice);
          else if (plainSpan) plainSpan.textContent = formatMoney(newLinePrice);
        }
        // Optimistically update subtotal and progress bar
        optimisticSubtotalUpdate(key, newQty);
      }
    }
  }

  function optimisticSubtotalUpdate(changedKey, newQty) {
    if (!state.cart) return;
    var subtotal = 0;
    var count = 0;
    state.cart.items.forEach(function (item) {
      if (item.variant_id === CHECKOUT_PLUS_VARIANT) return;
      var qty = (item.key === changedKey) ? newQty : item.quantity;
      if (qty <= 0) return;
      var unitPrice = item.final_line_price / item.quantity;
      subtotal += unitPrice * qty;
      count += qty;
    });
    var labelEl = document.getElementById('po-cart-subtotal-label');
    var amountEl = document.getElementById('po-cart-subtotal-amount');
    if (labelEl) labelEl.textContent = 'Subtotal (' + count + ' ' + (count === 1 ? 'item' : 'items') + ')';
    if (amountEl) amountEl.textContent = formatMoney(subtotal);
    // Update progress bar — mirror renderProgressBar's tierApplies filter so
    // optimistic updates don't briefly show a tier that was just disqualified.
    var subtotalDollars = subtotal / 100;
    var section = document.getElementById('po-cart-progress');
    var fill = document.getElementById('po-cart-progress-fill');
    var text = document.getElementById('po-cart-progress-text');
    if (fill && text && CONFIG && CONFIG.progress_bar && CONFIG.progress_bar.tiers) {
      var displayItemsForTiers = state.cart.items
        .filter(function (item) { return item.variant_id !== CHECKOUT_PLUS_VARIANT; })
        .map(function (item) {
          if (item.key === changedKey) return Object.assign({}, item, { quantity: newQty });
          return item;
        })
        .filter(function (item) { return item.quantity > 0; });
      var tiers = CONFIG.progress_bar.tiers.filter(function (t) { return tierApplies(t, displayItemsForTiers); });
      tiers.sort(function (a, b) { return a.threshold - b.threshold; });
      if (tiers.length === 0) {
        if (section) section.classList.remove('is-visible');
      } else {
        if (section) section.classList.add('is-visible');
        var keywordTiers = tiers.filter(function (t) { return t.requires_keyword && t.requires_keyword.length > 0; });
        var activeTier = null;
        for (var i = 0; i < keywordTiers.length; i++) {
          if (subtotalDollars < keywordTiers[i].threshold) { activeTier = keywordTiers[i]; break; }
        }
        if (activeTier === null) for (var i = 0; i < tiers.length; i++) {
          if (subtotalDollars < tiers[i].threshold) { activeTier = tiers[i]; break; }
        }
        if (!activeTier) {
          fill.style.width = '100%';
          fill.classList.add('is-unlocked');
          text.textContent = tiers[tiers.length - 1].message_unlocked;
        } else {
          var pct = Math.min((subtotalDollars / activeTier.threshold) * 100, 100);
          fill.style.width = pct + '%';
          fill.classList.remove('is-unlocked');
          var remaining = (activeTier.threshold - subtotalDollars).toFixed(2);
          text.textContent = activeTier.message_progress.replace('${remaining}', formatDollars(remaining));
        }
      }
    }
    // Update checkout button total
    var totalEl = document.getElementById('po-cart-btn-plus-total');
    if (totalEl) {
      var cpPrice = CONFIG && CONFIG.checkout_plus ? Math.round(CONFIG.checkout_plus.price * 100) : 198;
      totalEl.textContent = formatMoney(subtotal + cpPrice);
    }
  }

  function updateQuantity(key, quantity) {
    dbg('updateQuantity() key:', key, 'qty:', quantity);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change.js failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        // Use the response directly — no need for a second fetch
        dbg('Quantity updated, items:', cart.item_count);
        state.cart = cart;
        state.cartFetchedAt = Date.now();
        renderCart(cart);
      })
      .catch(function (err) {
        console.error('[CartDrawer] Failed to update quantity:', err);
      });
  }

  // ============================================================
  // Section F — Subtotal
  // ============================================================

  function renderSubtotal(cart) {
    var labelEl = document.getElementById('po-cart-subtotal-label');
    var amountEl = document.getElementById('po-cart-subtotal-amount');
    if (!labelEl || !amountEl) return;

    var displayItems = cart.items.filter(function (item) {
      return item.variant_id !== CHECKOUT_PLUS_VARIANT;
    });

    var count = displayItems.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);

    var subtotal = displayItems.reduce(function (sum, item) {
      return sum + item.final_line_price;
    }, 0);

    var originalTotal = displayItems.reduce(function (sum, item) {
      return sum + item.original_line_price;
    }, 0);

    var itemWord = count === 1 ? 'item' : 'items';
    labelEl.textContent = 'Subtotal (' + count + ' ' + itemWord + ')';

    if (originalTotal > subtotal) {
      amountEl.innerHTML = '<span class="po-cart-item-price-sale">' + formatMoney(subtotal) + '</span> ' +
        '<span class="po-cart-item-price-compare">' + formatMoney(originalTotal) + '</span>';
    } else {
      amountEl.textContent = formatMoney(subtotal);
    }
  }

  // ============================================================
  // Section G — Announcement Bar
  // ============================================================

  function renderAnnouncement() {
    var el = document.getElementById('po-cart-announcement');
    if (!el || !CONFIG) return;

    // Free shipping is US-only (Chris 2026-10-11): international carts don't see a shipping announcement
    var shippingLine = CONFIG.announcement && /shipping/i.test(CONFIG.announcement.text || '');
    if (CONFIG.announcement && CONFIG.announcement.enabled && !(shippingLine && !shopperIsUS())) {
      el.textContent = CONFIG.announcement.text;
      el.classList.add('is-visible');
    } else {
      el.classList.remove('is-visible');
    }
  }

  // ============================================================
  // Section H — Progress Bar
  // ============================================================

  function tierApplies(tier, displayItems) {
    // Free shipping is US-only (Chris 2026-10-11): no free-shipping progress for international carts
    if (tier.type === 'free_shipping' && !shopperIsUS()) return false;
    // A tier with `requires_keyword` is only active when at least one cart
    // item title matches one of the keywords. Used for collection-conditional
    // tiers like the Roger Clemens signed baseball, which should disappear
    // the moment the qualifying product is removed from the cart.
    if (!tier.requires_keyword || tier.requires_keyword.length === 0) return true;
    var titles = displayItems.map(function (i) {
      return ((i.product_title || '') + ' ' + (i.title || '') + ' ' + (i.handle || '')).toLowerCase();
    });
    return tier.requires_keyword.some(function (kw) {
      var k = String(kw).toLowerCase();
      return titles.some(function (t) { return t.indexOf(k) !== -1; });
    });
  }

  function renderProgressBar(cart) {
    var section = document.getElementById('po-cart-progress');
    var fill = document.getElementById('po-cart-progress-fill');
    var text = document.getElementById('po-cart-progress-text');
    if (!section || !fill || !text || !CONFIG || !CONFIG.progress_bar) return;

    var allTiers = CONFIG.progress_bar.tiers;
    if (!allTiers || allTiers.length === 0) {
      section.classList.remove('is-visible');
      return;
    }

    var displayItems = cart.items.filter(function (item) {
      return item.variant_id !== CHECKOUT_PLUS_VARIANT;
    });

    // Filter tiers by their requires_keyword condition before any further work.
    var tiers = allTiers.filter(function (t) { return tierApplies(t, displayItems); });
    if (tiers.length === 0) {
      section.classList.remove('is-visible');
      return;
    }

    tiers.sort(function (a, b) { return a.threshold - b.threshold; });

    var subtotalCents = displayItems.reduce(function (sum, item) {
      return sum + item.final_line_price;
    }, 0);
    var subtotalDollars = subtotalCents / 100;

    // Tiers with requires_keyword (e.g. Roger Clemens signed baseball) take
    // priority over generic ones — they only show when the customer qualifies,
    // and they're the more compelling pitch for that customer.
    var keywordTiers = tiers.filter(function (t) { return t.requires_keyword && t.requires_keyword.length > 0; });
    var activeTier = null;
    for (var i = 0; i < keywordTiers.length; i++) {
      if (subtotalDollars < keywordTiers[i].threshold) {
        activeTier = keywordTiers[i];
        break;
      }
    }
    if (activeTier === null) for (var i = 0; i < tiers.length; i++) {
      if (subtotalDollars < tiers[i].threshold) {
        activeTier = tiers[i];
        break;
      }
    }

    if (!activeTier) {
      fill.style.width = '100%';
      fill.classList.add('is-unlocked');
      text.textContent = tiers[tiers.length - 1].message_unlocked;
    } else {
      var pct = Math.min((subtotalDollars / activeTier.threshold) * 100, 100);
      fill.style.width = pct + '%';
      fill.classList.remove('is-unlocked');
      var remaining = (activeTier.threshold - subtotalDollars).toFixed(2);
      text.textContent = activeTier.message_progress.replace('${remaining}', formatDollars(remaining));
    }

    section.classList.add('is-visible');
  }

  // ============================================================
  // Section I — Checkout+ Section
  // ============================================================

  function renderCheckoutPlus(cart) {
    var section = document.getElementById('po-cart-checkout-plus');
    if (!section || !CONFIG || !CONFIG.checkout_plus || !CONFIG.checkout_plus.enabled) return;

    var displayItems = cart.items.filter(function (item) {
      return item.variant_id !== CHECKOUT_PLUS_VARIANT;
    });

    var allNonReturnable = !shopperIsUS() || (displayItems.length > 0 && displayItems.every(function (item) {
      return isNonReturnable(item);
    }));

    var btnPlus = document.getElementById('po-cart-btn-plus');
    var btnWithout = document.getElementById('po-cart-btn-without');

    if (allNonReturnable) {
      section.classList.remove('is-visible');
      // Change checkout+ button to just "CHECKOUT"
      if (btnPlus) {
        btnPlus.innerHTML = 'CHECKOUT';
      }
      if (btnWithout) {
        btnWithout.style.display = 'none';
      }
      return;
    }

    // Show section
    section.classList.add('is-visible');
    if (btnWithout) {
      btnWithout.style.display = '';
    }

    // Logo
    var logo = document.getElementById('po-cart-cp-logo');
    if (logo) {
      logo.src = CONFIG.checkout_plus.logo_url;
    }

    // Price
    var priceEl = section.querySelector('.po-cart-cp-price');
    if (priceEl) {
      priceEl.textContent = formatDollars(CONFIG.checkout_plus.price);
    }

    // Checkout+ is only added when the CHECKOUT+ button is clicked — not auto-added

    // Tooltip
    renderTooltip();
  }

  // Shopper country from localization (on #po-cart-checkout-plus). Checkout+ (free returns) and free
  // shipping are US-only (Chris 2026-10-11). Missing attribute = old snippet, treat as US.
  function shopperIsUS() {
    var section = document.getElementById('po-cart-checkout-plus');
    var country = section && section.getAttribute('data-country');
    return !country || country === 'US';
  }

  function isNonReturnable(item) {
    if (!CONFIG || !CONFIG.checkout_plus) return false;
    var tags = CONFIG.checkout_plus.non_returnable_tags || [];

    // Check product_type
    var productType = (item.product_type || '').toLowerCase();
    var propsType = (item.properties && item.properties._product_type || '').toLowerCase();

    for (var i = 0; i < tags.length; i++) {
      var tag = tags[i].toLowerCase();
      if (productType.indexOf(tag) !== -1) return true;
      if (propsType.indexOf(tag) !== -1) return true;
      // Keyword match against title
      if (item.title && item.title.toLowerCase().indexOf(tag) !== -1) return true;
      if (item.product_title && item.product_title.toLowerCase().indexOf(tag) !== -1) return true;
    }

    return false;
  }

  function renderTooltip() {
    var tooltip = document.getElementById('po-cart-cp-tooltip');
    if (!tooltip || !CONFIG || !CONFIG.checkout_plus || !CONFIG.checkout_plus.tooltip) return;

    var tt = CONFIG.checkout_plus.tooltip;
    var html = '<div style="font-weight:700;font-size:14px;margin-bottom:8px;text-align:left;">' + escapeHtml(tt.title) + '</div>' +
      '<ul style="margin:0;padding:0;list-style:none;text-align:left;">';
    tt.benefits.forEach(function (b) {
      html += '<li style="padding:3px 0;font-size:12px;display:flex;align-items:flex-start;gap:6px;">' +
        '<span style="color:#f15822;font-weight:700;flex-shrink:0;">✓</span> ' + escapeHtml(b) + '</li>';
    });
    html += '</ul>';
    tooltip.innerHTML = html;
  }

  function setupTooltipListeners() {
    var infoBtn = document.querySelector('.po-cart-cp-info-btn');
    var tooltip = document.getElementById('po-cart-cp-tooltip');
    if (!infoBtn || !tooltip) return;

    infoBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      tooltip.classList.toggle('is-visible');
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.po-cart-cp-info-btn') && !e.target.closest('#po-cart-cp-tooltip')) {
        tooltip.classList.remove('is-visible');
      }
    });
  }

  // ============================================================
  // Section J — Checkout Buttons
  // ============================================================

  function renderButtons(cart) {
    var totalEl = document.getElementById('po-cart-btn-plus-total');
    var btnPlus = document.getElementById('po-cart-btn-plus');
    if (!totalEl || !btnPlus) return;

    var displayItems = cart.items.filter(function (item) {
      return item.variant_id !== CHECKOUT_PLUS_VARIANT;
    });

    var allNonReturnable = !shopperIsUS() || (displayItems.length > 0 && displayItems.every(function (item) {
      return isNonReturnable(item);
    }));

    if (allNonReturnable) {
      // Already handled in renderCheckoutPlus — button says "CHECKOUT"
      return;
    }

    // Restore the full Checkout+ button text
    var cpInCart = cart.items.some(function (item) {
      return item.variant_id === CHECKOUT_PLUS_VARIANT;
    });

    var subtotalCents = displayItems.reduce(function (sum, item) {
      return sum + item.final_line_price;
    }, 0);

    var total;
    if (cpInCart) {
      total = cart.total_price;
    } else {
      total = subtotalCents + Math.round(CONFIG.checkout_plus.price * 100);
    }

    btnPlus.innerHTML = 'CHECKOUT+ <span class="po-cart-btn-divider">|</span> <span id="po-cart-btn-plus-total">' + formatMoney(total) + '</span>';
  }

  function setupCheckoutListeners() {
    var btnPlus = document.getElementById('po-cart-btn-plus');
    var btnWithout = document.getElementById('po-cart-btn-without');

    if (btnPlus) {
      btnPlus.addEventListener('click', function () {
        btnPlus.innerHTML = '<span class="po-spinner"></span>';
        btnPlus.style.pointerEvents = 'none';

        var cart = state.cart;
        if (!cart) { window.location.href = getCheckoutUrl(); return; }

        var displayItems = cart.items.filter(function (item) {
          return item.variant_id !== CHECKOUT_PLUS_VARIANT;
        });
        var allNonReturnable = !shopperIsUS() || (displayItems.length > 0 && displayItems.every(function (item) {
          return isNonReturnable(item);
        }));

        if (allNonReturnable) {
          window.location.href = getCheckoutUrl();
          return;
        }

        // Check if Checkout+ is already in cart — don't add a duplicate
        var cpAlreadyInCart = cart.items.some(function (item) {
          return item.variant_id === CHECKOUT_PLUS_VARIANT;
        });
        if (cpAlreadyInCart) {
          dbg('Checkout+ already in cart, going straight to checkout');
          window.location.href = getCheckoutUrl();
          return;
        }

        // Form POST — adds Checkout+ and redirects to checkout in one server-side step
        dbg('Form POST: adding Checkout+ and redirecting to checkout');
        var form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart/add';
        form.style.display = 'none';
        var idInput = document.createElement('input');
        idInput.name = 'id';
        idInput.value = String(CHECKOUT_PLUS_VARIANT);
        form.appendChild(idInput);
        var qtyInput = document.createElement('input');
        qtyInput.name = 'quantity';
        qtyInput.value = '1';
        form.appendChild(qtyInput);
        var returnInput = document.createElement('input');
        returnInput.name = 'return_to';
        returnInput.value = '/checkout';
        form.appendChild(returnInput);
        document.body.appendChild(form);
        form.submit();
      });
    }

    if (btnWithout) {
      btnWithout.addEventListener('click', function () {
        btnWithout.innerHTML = '<span class="po-spinner po-spinner--dark"></span>';
        btnWithout.style.pointerEvents = 'none';

        // Use cached cart — no extra fetch needed
        var cart = state.cart;
        if (!cart) { window.location.href = getCheckoutUrl(); return; }

        var cpItem = cart.items.find(function (item) {
          return item.variant_id === CHECKOUT_PLUS_VARIANT;
        });

        if (cpItem) {
          var updates = {};
          updates[cpItem.key] = 0;
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ updates: updates })
          })
            .then(function () { window.location.href = getCheckoutUrl(); })
            .catch(function () { window.location.href = getCheckoutUrl(); });
        } else {
          window.location.href = getCheckoutUrl();
        }
      });
    }
  }

  // ============================================================
  // Section K — Upsell Recommendations
  // ============================================================

  function renderUpsell(cart) {
    dbg('renderUpsell() called');
    var section = document.getElementById('po-cart-upsell');
    if (!section || !CONFIG || !CONFIG.upsell || !CONFIG.upsell.enabled) {
      dbg('renderUpsell skipped:', !section ? 'no section' : !CONFIG ? 'no config' : 'upsell disabled');
      if (section) section.classList.remove('is-visible');
      return;
    }

    dbg('Upsell rules:', CONFIG.upsell.rules.length, 'rules');
    getRecommendedProducts(cart).then(function (products) {
      dbg('Upsell products found:', products ? products.length : 0);
      if (products && products.length > 0) {
        var heading = document.getElementById('po-cart-upsell-heading');
        if (heading) heading.textContent = CONFIG.upsell.heading;

        var container = document.getElementById('po-cart-upsell-products');
        if (!container) return;

        var maxProducts = CONFIG.upsell.max_products || 1;
        var shown = products.slice(0, maxProducts);

        container.innerHTML = '';
        shown.forEach(function (product) {
          container.innerHTML += renderUpsellCard(product);
          trackImpressionOnce(product._matchedRuleId, product.id, product._matchedRuleIndex);
        });

        // Horizontal scroll if multiple cards
        if (maxProducts > 1) {
          container.style.overflowX = 'auto';
          container.style.scrollSnapType = 'x mandatory';
          container.style.flexDirection = 'row';
          var cards = container.querySelectorAll('.po-cart-upsell-card');
          cards.forEach(function (card) {
            card.style.scrollSnapAlign = 'start';
            card.style.minWidth = '85%';
            card.style.flexShrink = '0';
          });
        }

        section.classList.add('is-visible');
      } else {
        section.classList.remove('is-visible');
      }
    }).catch(function (err) {
      console.error('[CartDrawer] Upsell error:', err);
      if (section) section.classList.remove('is-visible');
    });
  }

  function getRecommendedProducts(cart) {
    if (!CONFIG || !CONFIG.upsell || !CONFIG.upsell.rules) {
      return Promise.resolve([]);
    }

    var rules = CONFIG.upsell.rules;
    var cartProductIds = cart.items.map(function (item) { return item.product_id; });
    var cartHandles = cart.items.map(function (item) { return item.handle; });
    var exclusions = CONFIG.upsell.exclusions || [];

    return evaluateRules(rules, 0, cart, cartProductIds, cartHandles, exclusions, []);
  }

  // Defensive shape normalizer. The PO Upsell Dashboard publish step writes
  // rules with cart filters nested inside `condition` and renames `products`
  // to `output_product_ids`. This function lifts the fields back to the
  // top-level shape the rest of evaluateRules expects, so the storefront
  // works regardless of which writer last touched cart-config.json.
  function normalizeRule(raw) {
    if (!raw) return raw;
    var rule = {};
    for (var k in raw) { if (Object.prototype.hasOwnProperty.call(raw, k)) rule[k] = raw[k]; }
    var cond = rule.condition || {};
    var liftedCond = { type: cond.type, handle: cond.handle };
    var nestedKeys = ['min_cart_value', 'max_cart_value', 'cart_has_keyword'];
    nestedKeys.forEach(function (k) {
      if (rule[k] === undefined && cond[k] !== undefined) rule[k] = cond[k];
    });
    rule.condition = liftedCond;
    if ((!rule.products || rule.products.length === 0) && rule.output_product_ids) {
      rule.products = rule.output_product_ids;
    }
    return rule;
  }

  function evaluateRules(rules, index, cart, cartProductIds, cartHandles, exclusions, accumulated) {
    if (index >= rules.length) {
      return Promise.resolve(accumulated);
    }

    var rule = normalizeRule(rules[index]);
    var maxProducts = CONFIG.upsell.max_products || 1;

    if (accumulated.length >= maxProducts) {
      return Promise.resolve(accumulated);
    }

    dbg('Evaluating rule', index, ':', rule.condition.type, rule.condition.handle || '',
      rule.min_cart_value ? 'min$' + rule.min_cart_value : '',
      rule.max_cart_value ? 'max$' + rule.max_cart_value : '',
      rule.cart_has_keyword ? 'keywords:' + rule.cart_has_keyword.join(',') : '');

    // Check cart value thresholds before collection condition
    var displayItems = cart.items.filter(function (i) { return i.variant_id !== CHECKOUT_PLUS_VARIANT; });
    var cartSubtotal = displayItems.reduce(function (s, i) { return s + i.final_line_price; }, 0) / 100;

    if (rule.min_cart_value !== undefined && cartSubtotal < rule.min_cart_value) {
      dbg('Rule', index, 'skipped: cart $' + cartSubtotal.toFixed(2) + ' below min $' + rule.min_cart_value);
      return evaluateRules(rules, index + 1, cart, cartProductIds, cartHandles, exclusions, accumulated);
    }
    if (rule.max_cart_value !== undefined && cartSubtotal > rule.max_cart_value) {
      dbg('Rule', index, 'skipped: cart $' + cartSubtotal.toFixed(2) + ' above max $' + rule.max_cart_value);
      return evaluateRules(rules, index + 1, cart, cartProductIds, cartHandles, exclusions, accumulated);
    }

    // Check cart keyword condition
    if (rule.cart_has_keyword && rule.cart_has_keyword.length > 0) {
      var cartTitles = displayItems.map(function (i) { return (i.product_title || i.title || '').toLowerCase(); });
      var hasKeyword = rule.cart_has_keyword.some(function (kw) {
        return cartTitles.some(function (t) { return t.indexOf(kw.toLowerCase()) !== -1; });
      });
      if (!hasKeyword) {
        dbg('Rule', index, 'skipped: no keyword match in cart titles');
        return evaluateRules(rules, index + 1, cart, cartProductIds, cartHandles, exclusions, accumulated);
      }
    }

    return checkCondition(rule.condition, cart, cartProductIds, cartHandles)
      .then(function (matched) {
        dbg('Rule', index, 'matched:', matched);
        if (!matched) {
          return evaluateRules(rules, index + 1, cart, cartProductIds, cartHandles, exclusions, accumulated);
        }

        // Condition matched — get products
        return getProductsForRule(rule, cart, cartProductIds)
          .then(function (products) {
            // Attach discount from rule to each product
            var discountPercent = rule.discount_percent || 0;
            var discountCode = rule.discount_code || '';
            products.forEach(function (p) {
              p._discountPercent = discountPercent;
              p._discountCode = discountCode;
              p._matchedRuleIndex = index;
              p._matchedRuleId = (rule.id !== undefined && rule.id !== null) ? rule.id : null;
            });

            // Filter: explicit exclusions only + OOS + dedupe against products
            // already accumulated from prior rules (so the same product never
            // shows as two side-by-side cards when multiple rules output it).
            // We intentionally allow recommending products already in cart —
            // the PO Widget Discount function scopes each discount to the
            // specific upsell-added line (CartLineTarget), so an upsell-added
            // duplicate gets the discount while the manually-added one stays
            // at full price.
            var accumulatedIds = {};
            for (var ai = 0; ai < accumulated.length; ai++) {
              accumulatedIds[accumulated[ai].id] = true;
            }
            var filtered = products.filter(function (p) {
              if (exclusions.indexOf(p.handle) !== -1) return false;
              if (accumulatedIds[p.id]) return false;
              if (CONFIG.upsell.filter_out_of_stock) {
                var hasAvailable = p.variants && p.variants.some(function (v) {
                  // Storefront /products/{handle}.json doesn't include 'available' field
                  // Treat undefined as available — only filter if explicitly false
                  return v.available !== false;
                });
                if (!hasAvailable) return false;
              }
              accumulatedIds[p.id] = true;
              return true;
            });

            var combined = accumulated.concat(filtered);

            if (rule.exit_if_matched) {
              return combined;
            }

            return evaluateRules(rules, index + 1, cart, cartProductIds, cartHandles, exclusions, combined);
          });
      });
  }

  function checkCondition(condition, cart, cartProductIds, cartHandles) {
    if (!condition) return Promise.resolve(false);

    if (condition.type === 'any') {
      return Promise.resolve(true);
    }

    if (condition.type === 'product') {
      var matched = cartHandles.indexOf(condition.handle) !== -1;
      return Promise.resolve(matched);
    }

    if (condition.type === 'collection') {
      return fetchCollection(condition.handle).then(function (collectionProducts) {
        if (!collectionProducts) return false;
        var collectionProductIds = collectionProducts.map(function (p) { return p.id; });
        return cartProductIds.some(function (id) {
          return collectionProductIds.indexOf(id) !== -1;
        });
      });
    }

    return Promise.resolve(false);
  }

  function fetchCollection(handle) {
    if (state.collectionCache[handle]) {
      return Promise.resolve(state.collectionCache[handle]);
    }

    // Check sessionStorage first to avoid repeat API calls
    var cacheKey = 'po_coll_' + handle;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var parsed = JSON.parse(cached);
        state.collectionCache[handle] = parsed;
        return Promise.resolve(parsed);
      }
    } catch (e) {}

    return fetch('/collections/' + handle + '/products.json?limit=250', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = data.products || [];
        state.collectionCache[handle] = products;
        try { sessionStorage.setItem(cacheKey, JSON.stringify(products.map(function(p) { return { id: p.id, handle: p.handle }; }))); } catch(e) {}
        return products;
      })
      .catch(function (err) {
        console.error('[CartDrawer] Failed to fetch collection:', handle, err);
        return null;
      });
  }

  function getProductsForRule(rule, cart, cartProductIds) {
    if (rule.products && rule.products.length > 0) {
      var promises = rule.products.map(function (handle) {
        return fetchProduct(handle);
      });
      return Promise.all(promises).then(function (results) {
        return results.filter(function (p) { return p !== null; });
      });
    }

    if (rule.source === 'shopify_recommendations') {
      var firstItem = cart.items.find(function (item) {
        return item.variant_id !== CHECKOUT_PLUS_VARIANT;
      });
      if (!firstItem) return Promise.resolve([]);

      return fetch('/recommendations/products.json?product_id=' + firstItem.product_id + '&limit=10', {
        credentials: 'same-origin'
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          return data.products || [];
        })
        .catch(function (err) {
          console.error('[CartDrawer] Failed to fetch recommendations:', err);
          return [];
        });
    }

    return Promise.resolve([]);
  }

  function fetchProduct(handle) {
    if (state.productCache[handle]) {
      return Promise.resolve(state.productCache[handle]);
    }

    // .json has the shape this file expects but no stock; .js has `available`.
    // If .js fails the card behaves as if everything is in stock.
    var stockFetch = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });

    return Promise.all([
      fetch('/products/' + handle + '.json', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) return null;
          return r.json();
        }),
      stockFetch,
    ])
      .then(function (results) {
        var data = results[0];
        var stock = results[1];
        if (!data || !data.product) return null;
        if (stock && stock.variants) {
          var soldOut = {};
          stock.variants.forEach(function (v) { soldOut[v.id] = v.available === false; });
          data.product.variants.forEach(function (v) {
            if (soldOut[v.id]) v.available = false;
          });
        }
        state.productCache[handle] = data.product;
        return data.product;
      })
      .catch(function (err) {
        console.error('[CartDrawer] Failed to fetch product:', handle, err);
        return null;
      });
  }

  function normalizePrice(price) {
    // Shopify /products/{handle}.json returns prices as dollar strings ("32.00")
    // Shopify /recommendations/products.json returns prices as cents (3200)
    // Detect: if price > 999, it's almost certainly cents
    var p = parseFloat(price);
    if (p > 999) return p / 100;
    return p;
  }

  function renderUpsellCard(product) {
    // Storefront API doesn't include 'available' — treat undefined as available
    var availableVariants = product.variants.filter(function (v) { return v.available !== false; });
    var isSingleVariant = availableVariants.length === 1;
    var hasMultipleVariants = availableVariants.length > 1;

    var firstVariant = availableVariants[0] || product.variants[0];
    var originalPrice = normalizePrice(firstVariant.price);
    var compareAt = firstVariant.compare_at_price ? normalizePrice(firstVariant.compare_at_price) : 0;
    var discountPercent = product._discountPercent || 0;

    // Show exactly what the cart will charge: the set price if this product
    // has one (shop metafield cro.drawer_set_prices, PO Widget Pricing charges
    // it), else the rule's percent off.
    var displayPrice = originalPrice;
    var showCompare = false;
    if (discountPercent > 0) {
      var setPrice = parseFloat((window.PO_DRAWER_SET_PRICES || {})[product.handle]);
      if (setPrice > 0 && setPrice < originalPrice) {
        displayPrice = setPrice;
      } else {
        displayPrice = Math.round(originalPrice * (100 - discountPercent)) / 100;
      }
      showCompare = true;
    } else if (compareAt > originalPrice) {
      displayPrice = originalPrice;
      showCompare = true;
    }

    var priceHtml = '';
    if (showCompare) {
      var strikePrice = discountPercent > 0 ? originalPrice : compareAt;
      priceHtml = '<span class="po-cart-upsell-price-sale">' + formatDollars(displayPrice) + '</span>' +
        '<span class="po-cart-upsell-price-compare">' + formatDollars(strikePrice) + '</span>';
    } else {
      priceHtml = '<span>' + formatDollars(displayPrice) + '</span>';
    }

    var imgSrc = '';
    if (product.images && product.images.length > 0) {
      imgSrc = product.images[0].src || product.images[0];
    } else if (product.featured_image) {
      imgSrc = product.featured_image;
    }

    var selectHtml = '';
    var variantIdAttr = '';
    var options = product.options || [];

    if (hasMultipleVariants && options.length > 1) {
      // Multi-option product (e.g., Tops Size + Bottoms Size)
      options.forEach(function (opt, idx) {
        selectHtml += '<select class="po-cart-upsell-option-select" data-product-id="' + product.id + '" data-option-index="' + (idx + 1) + '" style="width:100%;padding:6px 8px;border:1px solid #ddd;border-radius:4px;font-size:13px;margin-bottom:6px;background:#fff;">' +
          '<option value="">Select ' + escapeHtml(opt.name) + '</option>';
        opt.values.forEach(function (val) {
          var inStock = availableVariants.some(function (v) { return v['option' + (idx + 1)] === val; });
          if (inStock) selectHtml += '<option value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</option>';
        });
        selectHtml += '</select>';
      });
      // Hidden field to store matched variant ID
      selectHtml += '<input type="hidden" class="po-cart-upsell-variant-id" data-product-id="' + product.id + '" value="" />';
    } else if (hasMultipleVariants) {
      // Single-option product (e.g., just Size)
      selectHtml = '<select class="po-cart-upsell-variant-select" data-product-id="' + product.id + '">' +
        '<option value="">Select Size</option>';
      availableVariants.forEach(function (v) {
        selectHtml += '<option value="' + v.id + '">' + escapeHtml(v.option1 || v.title) + '</option>';
      });
      selectHtml += '</select>';
    }

    if (isSingleVariant) variantIdAttr = firstVariant.id;

    var html = '<div class="po-cart-upsell-card" data-product-id="' + product.id + '" data-rule-id="' + (product._matchedRuleId != null ? product._matchedRuleId : '') + '" data-rule-index="' + (product._matchedRuleIndex != null ? product._matchedRuleIndex : '') + '" data-discount-percent="' + (product._discountPercent || '') + '">' +
      '<img class="po-cart-upsell-img" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(product.title) + '" />' +
      '<div class="po-cart-upsell-info">' +
        '<p class="po-cart-upsell-title">' + escapeHtml(product.title) + '</p>' +
        '<p class="po-cart-upsell-price">' + priceHtml + '</p>' +
        selectHtml +
        '<button class="po-cart-upsell-add" data-variant-id="' + variantIdAttr + '">ADD</button>' +
      '</div>' +
    '</div>';

    return html;
  }

  // Multi-option cards: a value stays pickable only if an in-stock variant has
  // it alongside the choices already made in the dropdowns before it. A choice
  // that stops being possible is cleared, so ADD highlights the empty dropdown.
  function narrowLaterOptions(selects, product) {
    if (!product) return;
    var chosen = {};
    selects.forEach(function (sel) {
      var key = 'option' + sel.getAttribute('data-option-index');
      Array.prototype.forEach.call(sel.options, function (o) {
        if (!o.value) return;
        o.disabled = !product.variants.some(function (v) {
          if (v.available === false || v[key] !== o.value) return false;
          return Object.keys(chosen).every(function (k) { return v[k] === chosen[k]; });
        });
      });
      if (sel.value && sel.options[sel.selectedIndex].disabled) sel.value = '';
      if (sel.value) chosen[key] = sel.value;
    });
  }

  function setupUpsellListeners() {
    var section = document.getElementById('po-cart-upsell');
    if (!section) return;

    section.addEventListener('change', function (e) {
      // Single-option dropdown (e.g., just Size)
      var singleSelect = e.target.closest('.po-cart-upsell-variant-select');
      if (singleSelect) {
        var card = singleSelect.closest('.po-cart-upsell-card');
        var addBtn = card ? card.querySelector('.po-cart-upsell-add') : null;
        if (addBtn) {
          addBtn.setAttribute('data-variant-id', singleSelect.value || '');
        }
        if (singleSelect.value && card) {
          trackCartEvent('click', card.getAttribute('data-rule-id'), parseInt(card.getAttribute('data-product-id'), 10), card.getAttribute('data-rule-index'));
        }
        return;
      }

      // Multi-option dropdown (e.g., Tops Size + Bottoms Size)
      var optionSelect = e.target.closest('.po-cart-upsell-option-select');
      if (optionSelect) {
        var card = optionSelect.closest('.po-cart-upsell-card');
        if (!card) return;
        var productId = optionSelect.getAttribute('data-product-id');
        var allSelects = card.querySelectorAll('.po-cart-upsell-option-select');
        var cardProduct = state.productCache[Object.keys(state.productCache).find(function (k) {
          return state.productCache[k].id === parseInt(productId, 10);
        })];
        narrowLaterOptions(allSelects, cardProduct);
        var selectedOptions = {};
        var allSelected = true;
        allSelects.forEach(function (sel) {
          var idx = sel.getAttribute('data-option-index');
          if (sel.value) {
            selectedOptions['option' + idx] = sel.value;
          } else {
            allSelected = false;
          }
        });

        var addBtn = card.querySelector('.po-cart-upsell-add');
        if (!allSelected || !addBtn) {
          if (addBtn) addBtn.setAttribute('data-variant-id', '');
          return;
        }

        // Find matching variant from cached product data
        var product = state.productCache[Object.keys(state.productCache).find(function (k) {
          return state.productCache[k].id === parseInt(productId, 10);
        })];
        if (product) {
          var match = product.variants.find(function (v) {
            return v.available !== false && Object.keys(selectedOptions).every(function (key) {
              return v[key] === selectedOptions[key];
            });
          });
          if (match) {
            addBtn.setAttribute('data-variant-id', match.id);
            dbg('Matched variant:', match.title, match.id);
            trackCartEvent('click', card.getAttribute('data-rule-id'), parseInt(productId, 10), card.getAttribute('data-rule-index'));
          } else {
            addBtn.setAttribute('data-variant-id', '');
          }
        }
        return;
      }
    });

    section.addEventListener('click', function (e) {
      var btn = e.target.closest('.po-cart-upsell-add');
      if (!btn) return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) {
        // No variant selected — highlight the dropdowns
        var card = btn.closest('.po-cart-upsell-card');
        var selects = card ? card.querySelectorAll('select') : [];
        selects.forEach(function (sel) {
          if (!sel.value) {
            sel.style.border = '2px solid #f15822';
            setTimeout(function () { sel.style.border = ''; }, 1500);
          }
        });
        return;
      }

      btn.textContent = 'ADDING...';

      var upsellCard = btn.closest('.po-cart-upsell-card');
      var upsellRuleIndex = upsellCard ? upsellCard.getAttribute('data-rule-index') : '';
      var upsellRuleId = upsellCard ? upsellCard.getAttribute('data-rule-id') : '';
      var upsellProductId = upsellCard ? parseInt(upsellCard.getAttribute('data-product-id'), 10) : null;
      var upsellDiscountPercent = upsellCard ? (upsellCard.getAttribute('data-discount-percent') || '10') : '10';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_po_discount': upsellDiscountPercent,
            '_po_widget': 'cart',
            '_po_widget_id': '',
            '_po_rule_id': String(upsellRuleId || ''),
          }
        }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Add failed: ' + r.status);
          return r.json();
        })
        .then(function () {
          trackCartEvent('add_to_cart', upsellRuleId, upsellProductId, upsellRuleIndex);
          // Force refresh cart after adding
          return fetchCart(true);
        })
        .then(function (cart) {
          state.lastUpsellProductIds = '';
          renderCart(cart);
        })
        .catch(function (err) {
          console.error('[CartDrawer] Failed to add upsell:', err);
          btn.textContent = 'ADD';
        });
    });
  }

  // ============================================================
  // Section L — Add-to-Cart Interception
  // ============================================================

  function setupAddToCartInterception() {
    // Listen for the Impact theme's cart:change event.
    // The theme's ProductForm dispatches this on documentElement after a successful add-to-cart.
    // This is the proper integration point — no DOM element watching needed.
    document.documentElement.addEventListener('cart:change', function (event) {
      dbg('cart:change event detected, baseEvent:', event.detail && event.detail.baseEvent);
      if (event.detail && event.detail.cart) {
        // Update our cached cart with the theme's data
        state.cart = event.detail.cart;
        state.cartFetchedAt = Date.now();
      }
      // Open our drawer
      openDrawer();
    });

    // Also listen for variant:add in case cart:change doesn't fire
    document.addEventListener('variant:add', function () {
      dbg('variant:add event detected');
    });

    // If theme drawer element exists, hide it when it opens
    var themeDrawer = document.getElementById('cart-drawer');
    if (themeDrawer) {
      dbg('Theme drawer found — setting up observer');
      var observer = new MutationObserver(function () {
        if (themeDrawer.hasAttribute('open')) {
          dbg('Theme drawer OPENED — hiding it');
          themeDrawer.style.opacity = '0';
          themeDrawer.style.pointerEvents = 'none';
        }
      });
      observer.observe(themeDrawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  // ============================================================
  // Section M — Initialization
  // ============================================================

  // Intercept all cart modifications to keep our drawer in sync with Rebuy widgets
  function setupCartInterceptor() {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = (typeof arguments[0] === 'string') ? arguments[0] : '';
      var isCartAdd = url.indexOf('/cart/add') !== -1;
      var isCartMod = isCartAdd || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1;

      if (isCartMod) {
        return originalFetch.apply(this, arguments).then(function (response) {
          var cloned = response.clone();
          cloned.json().then(function () {
            if (state.isOpen) {
              // Drawer is open — re-render with fresh data
              dbg('Cart modified while drawer open, refreshing');
              fetchCart(true).then(function (cart) {
                state.lastUpsellProductIds = '';
                renderCart(cart);
              });
            } else if (isCartAdd) {
              // Drawer is closed and something was added — update badge and open drawer
              dbg('Cart add detected, opening drawer');
              openDrawer();
            }
          }).catch(function () {});
          return response;
        });
      }

      return originalFetch.apply(this, arguments);
    };
  }

  // Prevent Rebuy Smart Cart from showing — other Rebuy widgets unaffected
  function disableRebuySmartCart() {
    var noop = function () { dbg('Blocked Rebuy SmartCart'); };
    function patch() {
      if (window.Rebuy && window.Rebuy.SmartCart) {
        window.Rebuy.SmartCart.show = noop;
        window.Rebuy.SmartCart.open = noop;
        dbg('Rebuy SmartCart patched');
      } else {
        setTimeout(patch, 500);
      }
    }
    patch();
  }

  function preloadUpsellData() {
    if (!CONFIG || !CONFIG.upsell || !CONFIG.upsell.rules) return;
    dbg('Preloading upsell data');
    CONFIG.upsell.rules.forEach(function (rule) {
      // Preload collections
      if (rule.condition && rule.condition.type === 'collection' && rule.condition.handle) {
        fetchCollection(rule.condition.handle);
      }
      // Preload products
      if (rule.products) {
        rule.products.forEach(function (handle) {
          fetchProduct(handle);
        });
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    // Handle back/forward navigation — reset stale state
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        dbg('Page restored from cache — resetting state');
        resetButtonStates();
        closeDrawer();
        state.cartFetchedAt = 0;
        state.cpAddedThisSession = false;
        state.lastUpsellProductIds = '';
      }
    });

    dbg('DOMContentLoaded — initializing');
    dbg('Theme cart type:', window.themeVariables && window.themeVariables.settings ? window.themeVariables.settings.cartType : 'unknown');
    dbg('Theme drawer element:', document.getElementById('cart-drawer') ? 'found' : 'NOT FOUND');
    dbg('PO drawer element:', document.getElementById('po-cart-drawer') ? 'found' : 'NOT FOUND');
    loadConfig().then(function () {
      dbg('Setup: listeners');
      renderAnnouncement();
      setupDrawerListeners();
      setupQuantityListeners();
      setupTooltipListeners();
      setupCheckoutListeners();
      setupUpsellListeners();
      setupAddToCartInterception();
      setupCartInterceptor();
      disableRebuySmartCart();
      // Preload upsell collection + product data so it's instant when drawer opens
      preloadUpsellData();
      dbg('Setup: COMPLETE');
    });
  });

})();